import { QueryClient, QueryFunction } from "@tanstack/react-query";
import {
  ADULT_REQUIRED_CODE,
  ADULT_REQUIRED_EVENT,
  CREDITS_REQUIRED_CODE,
  CREDITS_REQUIRED_EVENT,
  type CreditsRequiredDetail,
} from "@shared/auth-codes";

/**
 * Throws an Error with a sanitized message if the response is not OK.
 * Only the JSON `error` field is propagated to the client error state —
 * raw HTML / stack traces from a 500 page never reach Sentry or error UI.
 *
 * Special handling:
 * - 403 ADULT_REQUIRED → profile-completion dialog event
 * - 402 CREDITS_REQUIRED → CreditWall event (CLICK-credits-02)
 * The error still throws so callers can react with their own UX.
 */
async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    let message = res.statusText;
    let code: string | undefined;
    let required: number | undefined;
    let balance: number | undefined;
    let action: string | undefined;
    try {
      const body = await res.clone().json();
      if (typeof body?.error === "string") {
        message = body.error;
      }
      if (typeof body?.code === "string") {
        code = body.code;
      }
      if (typeof body?.required === "number") {
        required = body.required;
      }
      if (typeof body?.balance === "number") {
        balance = body.balance;
      }
      if (typeof body?.action === "string") {
        action = body.action;
      }
    } catch {
      // non-JSON body — fall back to status text
    }
    if (res.status === 403 && code === ADULT_REQUIRED_CODE) {
      // Clear the dismissal flag so the dialog re-opens, then notify listeners.
      // AppLayout subscribes via window.addEventListener("troopn:adult-required").
      try {
        sessionStorage.removeItem("profile-completion-dismissed");
        window.dispatchEvent(new CustomEvent(ADULT_REQUIRED_EVENT));
      } catch {
        // sessionStorage / dispatch unavailable in non-browser contexts (SSR/tests)
      }
    }
    if (res.status === 402 && (code === CREDITS_REQUIRED_CODE || /insufficient credits/i.test(message))) {
      try {
        const detail: CreditsRequiredDetail = {
          action,
          cost: required,
          balance,
        };
        window.dispatchEvent(new CustomEvent(CREDITS_REQUIRED_EVENT, { detail }));
      } catch {
        // dispatch unavailable in non-browser contexts (SSR/tests)
      }
    }
    throw new Error(`${res.status}: ${message}`);
  }
}

const FETCH_TIMEOUT = 15000;

function fetchWithTimeout(
  url: string,
  options?: RequestInit,
  timeoutMs: number = FETCH_TIMEOUT,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const mergedSignal = options?.signal
    ? options.signal
    : controller.signal;

  return fetch(url, { ...options, signal: mergedSignal })
    .then((res) => {
      clearTimeout(timeoutId);
      return res;
    })
    .catch((err) => {
      clearTimeout(timeoutId);
      throw err;
    });
}

export type ApiRequestOptions = {
  /** Override the default 15s abort. Cover image gen needs ~120s. */
  timeoutMs?: number;
};

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: ApiRequestOptions,
): Promise<Response> {
  const res = await fetchWithTimeout(
    url,
    {
      method,
      headers: data ? { "Content-Type": "application/json" } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    },
    options?.timeoutMs ?? FETCH_TIMEOUT,
  );

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetchWithTimeout(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes — override per-query for immutable data
      gcTime: 30 * 60 * 1000, // 30 minutes (default is 5)
      retry: 2,
      retryDelay: (attemptIndex: number) =>
        Math.min(1000 * 2 ** attemptIndex, 10000),
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Seed the auth/user (and optionally /api/users/:id) cache with a fresh
 * server response. Use after a PATCH /api/auth/user so the UI reflects the
 * change immediately, even if a follow-up GET returns 304 with stale body.
 */
export function seedAuthUserCache(updatedUser: unknown, profileUserId?: string) {
  if (!updatedUser || typeof updatedUser !== "object") return;
  const merge = (prev: any) =>
    prev ? { ...prev, ...(updatedUser as object) } : updatedUser;
  queryClient.setQueryData(["/api/auth/user"], merge);
  if (profileUserId) {
    queryClient.setQueryData(["/api/users", profileUserId], merge);
  }
}

// Persist a narrow allowlist of public query responses to localStorage.
// User-scoped data (auth user, messages, trips, profile, expenses) is NEVER
// persisted — a compromised origin (XSS, malicious extension) reading
// localStorage must not get PII back from the cache.
const CACHE_KEY = "troopn-query-cache";
const MAX_AGE = 24 * 60 * 60 * 1000;
const SAFE_TO_PERSIST = new Set<string>([
  "/api/festivals/registry",
  "/api/routes/registry",
  "/api/entities",
]);

function isSafeKey(key: unknown): boolean {
  if (!Array.isArray(key)) return false;
  const head = key[0];
  return typeof head === "string" && SAFE_TO_PERSIST.has(head);
}

try {
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    const { timestamp, state } = JSON.parse(cached) as {
      timestamp: number;
      state: Record<string, unknown>;
    };
    if (Date.now() - timestamp < MAX_AGE) {
      for (const [key, data] of Object.entries(state)) {
        try {
          const parsedKey = JSON.parse(key);
          if (isSafeKey(parsedKey)) {
            queryClient.setQueryData(parsedKey, data);
          }
        } catch {
          // skip entries with malformed keys
        }
      }
    }
  }
} catch {
  // localStorage unavailable or corrupt — start fresh
}

setInterval(() => {
  try {
    const cache = queryClient
      .getQueryCache()
      .getAll()
      .filter((q) => q.state.status === "success" && isSafeKey(q.queryKey))
      .reduce(
        (acc, q) => {
          acc[JSON.stringify(q.queryKey)] = q.state.data;
          return acc;
        },
        {} as Record<string, unknown>,
      );
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ timestamp: Date.now(), state: cache }),
    );
  } catch {
    // localStorage unavailable or full — skip save
  }
}, 30000);
